import Layout from 'projects/components/Layout';
import CreateSmartShootBriefOnMount from 'projects/mutations/CreateSmartShootBriefOnMount';
import BriefEntry from 'projects/routes/BriefEntry';
import PreziBrief from 'projects/routes/prezi/PreziBrief';
import ProjectBrief from 'projects/routes/project/ProjectBrief';
import SmartShootBriefPayment from 'projects/routes/SmartShootBriefPayment';
import SmartShootPhotographyBrief from 'projects/routes/SmartShootPhotographyBrief';
import SmartShootProjectGetStarted from 'projects/routes/SmartShootProjectGetStarted';
import SmartShootVideoBrief from 'projects/routes/SmartShootVideoBrief';
import SquareOnlineStoreBrief from 'projects/routes/squareOnlineStore/SquareOnlineStoreBrief';
import SquarespaceBrief from 'projects/routes/squarespace/SquarespaceBrief';
import * as React from 'react';
import { hot } from 'react-hot-loader';
import { Redirect, Route, Switch } from 'react-router-dom';

import { getBriefType_structuredBrief_fields } from '__generated__/getBriefType';
import { CreateBriefOnMount } from 'projects/mutations/CreateBrief';
// Polyfill for IE11, can be dropped once we remove IE11 support
import 'url-search-params-polyfill';
import BriefRedirect from './BriefRedirect';
import SquarespaceApiBrief from './SquarespaceApiBrief';

export const routes = {
    briefStep: (brief: BriefType, step: number) => {
        let type = 'projects';
        switch (brief.fields.__typename) {
            case 'SquarespaceBriefFields':
                type = 'squarespace';
                break;
            case 'PreziBriefFields':
                type = 'prezi';
                break;
            case 'SquareOnlineStoreBriefFields':
                type = 'square-online-store';
                break;
        }
        return `/briefs/${type}/${brief.id}/${step}`;
    },

    project: (projectId: number) => `/projects/${projectId}`,
    yourWork: () => `/work`,
    projectCreate: () => `/projects/create`,
    support: () => `/help`,
    videoBrief: (briefId: string) => `/briefs/video-production/${briefId}`,
    photographyBriefPayment: (briefId: string) => `/briefs/photography/${briefId}/payment`,
    videoBriefPayment: (briefId: string) => `/briefs/video-production/${briefId}/payment`,
    smartShootProjectGetStarted: (briefId: string) => `/smartshoot-projects/${briefId}/get-started`,
    photoBrief: (briefId: string) => `/briefs/photography/${briefId}`,
};

export function redirectTo(url: string) {
    window.location.assign(url);
}

export function redirectToProject(projectId: number) {
    redirectTo(routes.project(projectId));
}

export function redirectToYourWork() {
    redirectTo(routes.yourWork());
}

export function redirectToSmartShootGetStarted(smartShootProjectId: string) {
    redirectTo(routes.smartShootProjectGetStarted(smartShootProjectId));
}

export const redirectLaunchedBrief = (brief: { projectIds: number[] }) => {
    const { projectIds } = brief;
    projectIds.length == 1 ? redirectToProject(projectIds[0]) : redirectToYourWork();
};

export const redirectLaunchedStructuredBrief = (structuredBrief: { foreignKeys: number[] }) => {
    const { foreignKeys } = structuredBrief;
    foreignKeys.length == 1 ? redirectToProject(foreignKeys[0]) : redirectToYourWork();
};

type BriefFieldsType = getBriefType_structuredBrief_fields['__typename'];
export interface BriefType {
    id: string;
    fields: { __typename: BriefFieldsType };
}

export const routeForBrief = (
    type: BriefFieldsType,
    briefId: string,
    queryParams?: string
): string => {
    switch (type) {
        case 'ProjectBriefFields':
            return `/briefs/projects/${briefId}${queryParams}`;
        case 'SquarespaceApiBriefFields':
            return `/squarespace/${briefId}${queryParams}`;
        case 'SquarespaceBriefFields':
            return `/briefs/squarespace/${briefId}${queryParams}`;
        case 'SmartShootVideoFields':
            return `/briefs/video-production/${briefId}${queryParams}`;
        case 'SmartShootPhotographyFields':
            return `/briefs/photography/${briefId}${queryParams}`;
        case 'PreziBriefFields':
            return `/briefs/prezi/${briefId}${queryParams}`;
        case 'SquareOnlineStoreBriefFields':
            return `/briefs/square-online-store/${briefId}${queryParams}`;
    }
};

const BriefRoutes = () => (
    <Layout>
        <Switch>
            <Route path="/briefs/projects/create" component={BriefEntry} />
            <Route path={`/briefs/projects/:briefId/:step(1|2|3|4|5)`} component={ProjectBrief} />
            <Route
                path={`/briefs/projects/:briefId`}
                render={({ match }) => (
                    <Redirect
                        to={routes.briefStep(
                            {
                                id: match.params.briefId,
                                fields: { __typename: 'ProjectBriefFields' },
                            },
                            1
                        )}
                    />
                )}
            />
            <Route
                path="/briefs/squarespace/create"
                render={() => <CreateBriefOnMount fields={{ squarespace: {} }} />}
            />
            <Route
                path={`/briefs/squarespace/:briefId/:step(1|2|3|4|5)`}
                component={SquarespaceBrief}
            />
            <Route
                path={`/briefs/squarespace/:briefId`}
                render={({ match, location }) => (
                    <Redirect
                        to={
                            routes.briefStep(
                                {
                                    id: match.params.briefId,
                                    fields: { __typename: 'SquarespaceBriefFields' },
                                },
                                1
                            ) + location.search
                        }
                    />
                )}
            />

            <Route
                path="/briefs/prezi/create"
                render={() => <CreateBriefOnMount fields={{ prezi: {} }} />}
            />
            <Route path={`/briefs/prezi/:briefId/:step(1|2|3|4|5)`} component={PreziBrief} />
            <Route
                path={`/briefs/prezi/:briefId`}
                render={({ match }) => (
                    <Redirect
                        to={routes.briefStep(
                            {
                                id: match.params.briefId,
                                fields: { __typename: 'PreziBriefFields' },
                            },
                            1
                        )}
                    />
                )}
            />
            <Route
                path="/briefs/square-online-store/create"
                render={() => <CreateBriefOnMount fields={{ squareOnlineStore: {} }} />}
            />
            <Route
                path={`/briefs/square-online-store/:briefId/:step(1|2|3|4|5)`}
                component={SquareOnlineStoreBrief}
            />
            <Route
                path={`/briefs/square-online-store/:briefId`}
                render={({ match }) => (
                    <Redirect
                        to={routes.briefStep(
                            {
                                id: match.params.briefId,
                                fields: { __typename: 'SquareOnlineStoreBriefFields' },
                            },
                            1
                        )}
                    />
                )}
            />
            <Route
                path="/briefs/video-production/create"
                render={() => <CreateSmartShootBriefOnMount type="video" />}
            />
            <Route
                path="/briefs/video-production/:briefId"
                render={props => <SmartShootVideoBrief briefId={props.match.params.briefId} />}
                exact
            />
            <Route
                path="/briefs/photography/create"
                render={() => <CreateSmartShootBriefOnMount type="photo" />}
            />
            <Route
                path="/briefs/photography/:briefId"
                render={props => (
                    <SmartShootPhotographyBrief briefId={props.match.params.briefId} />
                )}
                exact
            />
            <Route
                path="/briefs/:smartShootBrief(video-production|photography)/:briefId/payment"
                render={props => (
                    <SmartShootBriefPayment
                        briefId={props.match.params.briefId}
                        briefType={props.match.params.smartShootBrief}
                    />
                )}
            />
        </Switch>
    </Layout>
);

const Routes = () => (
    <Switch>
        <Route exact path="/briefs/:briefId" component={BriefRedirect} />
        <Route path="/briefs" component={BriefRoutes} />
        <Route path="/squarespace/:briefId" component={SquarespaceApiBrief} />
        <Route
            path="/smartshoot-projects/:projectId/get-started"
            render={props => (
                <SmartShootProjectGetStarted projectId={props.match.params.projectId} />
            )}
        />
    </Switch>
);

export default hot(module)(Routes);
